import React from 'react';
import CanUseFeature from '@dashboard/components/CanUseFeature';
import Card from '@dashboard/components/Card';
import CardHeader from '@dashboard/components/CardHeader';
import CardHeaderTitle from '@dashboard/components/CardHeaderTitle';
import CardContent from '@dashboard/components/CardContent';
import SettingsContainer from '@dashboard/components/SettingsContainer';
import { useToast } from '@dashboard/context/ToastContext';
import Button from '@dashboard/components/Button';
import CopyElement from '@dashboard/components/CopyElement';
import { CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import Features from '@dashboard/enums/features';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import SettingsDivider from '@dashboard/components/SettingsDivider';
import useSellerQueries from '@dashboard/hooks/queries/useSellerQueries';
import { useMutation } from '@tanstack/react-query';
import Callout from '@dashboard/components/Callout';
import Spinner from '@dashboard/components/Spinner';
import { Form } from '@dashboard/components/Formik';
import timezones from '@dashboard/services/timezones';
import getGmtOffsetForTimezone from '@dashboard/services/getGmtOffsetForTimezone';
import * as Yup from 'yup';
import SettingsDescription from '@dashboard/components/SettingsDescription';
import SettingsHeader from '@dashboard/components/SettingsHeader';
import Link from '@/dashboard/components/Link';

export default function SettingsEmail() {
  const { seller } = useAuth();
  const { updateSeller } = useSellerQueries();

  return (
    <Card variant="root">
      <CardHeader variant="root">
        <div className="flex flex-col max-w-lg">
          <CardHeaderTitle variant="root">Email</CardHeaderTitle>
          <SettingsDescription className="mt-2">
            Manage your store's email timezone and send emails from your own domain.{' '}
            <Link href="/help/articles/custom-email-domain" color="default" blank>
              Learn more
            </Link>
          </SettingsDescription>
        </div>
      </CardHeader>

      <CardContent variant="root" className="mt-4 mb-32">
        <SettingsContainer size="lg">
          <SettingsHeader id="email-settings">Email settings</SettingsHeader>

          <Form
            enableReinitialize
            initialValues={{
              emailTimezoneFormat: seller.emailTimezoneFormat || 'Etc/UTC',
              emailShowTimezone: seller.emailShowTimezone,
            }}
            validationSchema={Yup.object().shape({
              emailTimezoneFormat: Yup.string().oneOf(timezones),
              emailShowTimezone: Yup.boolean(),
            })}
            fields={[
              {
                name: 'emailTimezoneFormat',
                required: false,
                type: 'select',
                label: 'Email timezone format',
                tooltip: 'The timezone format used for dates in emails to your customers',
                options: timezones.map(tz => ({
                  value: tz,
                  label: `${tz} (GMT ${getGmtOffsetForTimezone(tz)})`
                    .replace(/_/g, ' ')
                    .replace('/', ' - ')
                    .replace('Etc - UTC', 'UTC'),
                })),
              },
              {
                name: 'emailShowTimezone',
                required: false,
                type: 'checkbox',
                label: 'Show date timezone in emails',
                tooltip:
                  'Show date timezone in emails to your customers (e.g. 01 Jan, 2024, 09:40 (GMT-5))',
              },
            ]}
            onSubmit={async values => {
              try {
                const { data } = await updateSeller(values);

                return {
                  data,
                };
              } catch (error) {
                return {
                  error,
                };
              }
            }}
          />
        </SettingsContainer>

        <SettingsContainer size="lg">
          <SettingsDivider />
        </SettingsContainer>

        <SettingsContainer size="2xl">
          <CanUseFeature feature={Features.EMAIL_DOMAIN}>
            <SettingsHeader id="email-domain">Email domain</SettingsHeader>

            <SettingsDescription className="mb-4">
              Send emails from your own domain instead of{' '}
              <span className="font-medium">payments@checkoutpage.com</span>
            </SettingsDescription>

            <h3
              className="text-base leading-tight font-bold text-gray-900 flex items-start mb-2"
              id="email-domain"
            >
              1. Add your email domain
            </h3>

            <EmailDomainSettings />

            {seller.emailDomain &&
              seller.emailDomain?.dkimVerified === true &&
              seller.emailDomain?.returnPathDomainVerified === true && (
                <>
                  <SettingsDivider />

                  <h3
                    className="text-base leading-tight font-bold text-gray-900 flex items-start mb-2"
                    id="email-domain-verification"
                  >
                    2. Add a sender signature
                  </h3>

                  <SenderSignatureSettings />
                </>
              )}
          </CanUseFeature>
        </SettingsContainer>
      </CardContent>
    </Card>
  );
}

/*
  states
  1. no domain, form to add domain
  2.1. domain exists
    - status of domain records
    - instructions to verify domain with DNS
    - button to verify domain
    - button to remove domain
  2.2. domain verified
    - status of domain records
    - button to remove domain
*/

function EmailDomainSettings() {
  const { seller } = useAuth();

  if (!seller.emailDomain) {
    return <EmailDomainForm />;
  }

  return <EmailDomainConfiguration />;
}

function EmailDomainForm() {
  const { seller, setSeller } = useAuth();

  const { createEmailDomain } = useSellerQueries();
  const { emitToast } = useToast();

  const create = useMutation({
    mutationFn: createEmailDomain,
    onSuccess: data => {
      emitToast({
        title: 'Email domain created',
        description: 'Your email domain has been created',
        type: 'success',
      });

      setSeller(data);
    },
  });

  return (
    <Form
      initialValues={{
        domainName: seller.emailDomain?.name || '',
      }}
      fields={[
        {
          name: 'domainName',
          type: 'text',
          label: 'Domain name',
          placeholder: 'example.com',
          required: true,
        },
      ]}
      onSubmit={async values => {
        try {
          const response = await create.mutateAsync({
            emailDomain: values.domainName,
          });

          return {
            data: response.data,
          };
        } catch (err) {
          return {
            error: err.message,
          };
        }
      }}
      submitButton={{
        label: 'Add email domain',
      }}
    />
  );
}

function EmailDomainConfiguration() {
  const { seller, setSeller } = useAuth();

  const { verifyEmailDomain, deleteEmailDomain } = useSellerQueries();
  const { emitToast } = useToast();

  const verify = useMutation({
    mutationFn: verifyEmailDomain,
    onSuccess: data => {
      if (
        data.emailDomain?.dkimVerified === true &&
        data.emailDomain?.returnPathDomainVerified === true
      ) {
        emitToast({
          title: 'Email domain verified',
          description: 'Your email domain has been verified',
          type: 'success',
        });
      } else {
        emitToast({
          title: 'Email domain verification failed',
          description: 'Your email domain has not been verified, please try again',
          type: 'error',
        });
      }

      setSeller(data);
    },
  });

  const deleteEmailDomainMutation = useMutation({
    mutationFn: deleteEmailDomain,
    onSuccess: data => {
      emitToast({
        title: 'Email domain deleted',
        description: 'Your email domain has been deleted',
        type: 'success',
      });

      setSeller(data);
    },
  });

  const getHostName = hostName => {
    const domain = seller.emailDomain?.name;

    return hostName.replace(`.${domain}`, '');
  };

  return (
    <div>
      <SettingsDescription>
        Email DNS settings for <span className="font-medium">{seller.emailDomain?.name}</span>
      </SettingsDescription>

      <div className="space-y-2 text-sm">
        <div className="grid grid-cols-12 gap-x-2 border-b border-gray-200 py-2">
          <div className="col-span-2">Record</div>
          <div className="col-span-2">Type</div>
          <div className="col-span-4">Name (Host)</div>
          <div className="col-span-4">Value</div>
        </div>

        <div className="grid grid-cols-12 gap-x-2 py-2">
          <div className="col-span-2">
            <div className="inline-flex gap-x-1 items-center">
              {seller.emailDomain?.dkimVerified === false && (
                <ExclamationCircleIcon className="w-4 h-4 text-red-400" />
              )}

              {seller.emailDomain?.dkimVerified === true && (
                <CheckCircleIcon className="w-4 h-4 text-green-300" />
              )}

              <div>DKIM</div>
            </div>
          </div>
          <div className="col-span-2">
            <div>TXT</div>
          </div>
          <div className="col-span-4 truncate">
            <CopyElement
              text={getHostName(
                seller.emailDomain?.dkimPendingHost || seller.emailDomain?.dkimHost,
              )}
            />
          </div>
          <div className="col-span-4 truncate">
            <CopyElement
              text={seller.emailDomain?.dkimPendingTextValue || seller.emailDomain?.dkimTextValue}
            />
          </div>
        </div>

        <div className="grid grid-cols-12 gap-x-2 py-2">
          <div className="col-span-2">
            <div className="inline-flex gap-x-1 items-center">
              {seller.emailDomain?.returnPathDomainVerified === false && (
                <ExclamationCircleIcon className="w-4 h-4 text-red-400" />
              )}

              {seller.emailDomain?.returnPathDomainVerified === true && (
                <CheckCircleIcon className="w-4 h-4 text-green-300" />
              )}

              <div>Return-Path</div>
            </div>
          </div>
          <div className="col-span-2">
            <div>CNAME</div>
          </div>

          <div className="col-span-4 truncate">
            <CopyElement text={getHostName(seller.emailDomain?.returnPathDomain)} />
          </div>
          <div className="col-span-4 truncate">
            <CopyElement text={seller.emailDomain?.returnPathDomainCnameValue} />
          </div>
        </div>
      </div>

      {(seller.emailDomain?.dkimVerified === false ||
        seller.emailDomain?.returnPathDomainVerified === false) && (
        <SettingsContainer size="lg">
          <Callout variant="info">
            <Callout.Heading>Not yet verified</Callout.Heading>
            <Callout.Content>
              Add the above records to your DNS to verify your email domain
            </Callout.Content>
          </Callout>
        </SettingsContainer>
      )}

      {seller.emailDomain?.dkimVerified === true &&
        seller.emailDomain?.returnPathDomainVerified === true && (
          <SettingsContainer size="lg">
            <Callout variant="success">
              <Callout.Heading>Verified</Callout.Heading>
              <Callout.Content>
                Your email domain has been verified. Emails can be sent from{' '}
                <span className="font-medium">{seller.emailDomain?.name}</span>
              </Callout.Content>
            </Callout>
          </SettingsContainer>
        )}

      <div className="mt-4 flex gap-x-2">
        {(seller.emailDomain?.dkimVerified === false ||
          seller.emailDomain?.returnPathDomainVerified === false) && (
          <Button appearance="primary" onClick={() => verify.mutate()}>
            Verify domain
            {verify.isLoading && <Spinner size="small" className="ml-2 text-white" />}
          </Button>
        )}

        <Button appearance="secondary" onClick={() => deleteEmailDomainMutation.mutate()}>
          Delete domain
          {deleteEmailDomainMutation.isLoading && (
            <Spinner size="small" className="ml-2 text-black" />
          )}
        </Button>
      </div>
    </div>
  );
}

function SenderSignatureSettings() {
  const { seller } = useAuth();

  if (!seller.senderSignature) {
    return <SenderSignatureForm />;
  }

  return <SenderSignatureConfiguration />;
}

function SenderSignatureForm() {
  const { seller, setSeller } = useAuth();

  const { createSenderSignature } = useSellerQueries();
  const { emitToast } = useToast();

  const create = useMutation({
    mutationFn: createSenderSignature,
    onSuccess: data => {
      emitToast({
        title: 'Sender signature created',
        description: 'Your sender signature has been created',
        type: 'success',
      });

      setSeller(data);
    },
  });

  return (
    <>
      <SettingsDescription className="mb-4">
        Add the email address and from name that will be used to send emails to your customers.
      </SettingsDescription>

      <Form
        initialValues={{
          emailAddress: seller.senderSignature?.emailAddress || '',
          name: seller.senderSignature?.name || '',
        }}
        fields={[
          {
            name: 'emailAddress',
            type: 'email',
            label: 'From email',
            placeholder: 'Email address',
            required: true,
          },
          {
            name: 'name',
            type: 'text',
            label: 'From name',
            placeholder: 'Name',
            required: true,
          },
        ]}
        onSubmit={async values => {
          // check if email is same domain as emailDomain
          const emailAddress = values.emailAddress;
          const emailDomain = seller.emailDomain?.name;

          if (!emailAddress.endsWith(emailDomain)) {
            return {
              error: 'Email address must be from the same domain as your email domain',
            };
          }

          try {
            const response = await create.mutateAsync({
              emailAddress: values.emailAddress,
              name: values.name,
            });

            return {
              data: {
                emailAddress: response.data.senderSignature.emailAddress,
                name: response.data.senderSignature.name,
              },
            };
          } catch (err) {
            return {
              error: err.message,
            };
          }
        }}
        submitButton={{
          label: 'Add sender signature',
        }}
      />
    </>
  );
}

function SenderSignatureConfiguration() {
  const { seller, user, setSeller } = useAuth();

  const { verifySenderSignature, deleteSenderSignature, sendTestEmail } = useSellerQueries();
  const { emitToast } = useToast();

  const verify = useMutation({
    mutationFn: verifySenderSignature,
    onSuccess: data => {
      emitToast({
        title: 'Sender signature verified',
        description: 'Your sender signature has been verified',
        type: 'success',
      });

      setSeller(data);
    },
  });

  const deleteSenderSignatureMutation = useMutation({
    mutationFn: deleteSenderSignature,
    onSuccess: data => {
      emitToast({
        title: 'Sender signature deleted',
        description: 'Your sender signature has been deleted',
        type: 'success',
      });

      setSeller(data);
    },
  });

  const sendTestEmailMutation = useMutation({
    mutationFn: sendTestEmail,
    onSuccess: () => {
      emitToast({
        title: 'Test email sent',
        description: `A test email has been sent to ${user.email}`,
        type: 'success',
      });
    },
  });

  return (
    <div>
      <SettingsDescription>
        The name and email address that emails will be sent from
      </SettingsDescription>

      <div className="mt-4">
        <div className="flex gap-x-1 items-center">
          <div>
            {seller.senderSignature?.confirmed === false && (
              <ExclamationCircleIcon className="w-4 h-4 text-red-400" />
            )}

            {seller.senderSignature?.confirmed === true && (
              <CheckCircleIcon className="w-4 h-4 text-green-300" />
            )}
          </div>

          <div>
            <div>
              {seller.senderSignature?.name}{' '}
              <span className="font-medium">{`<${seller.senderSignature?.emailAddress}>`}</span>
            </div>
          </div>
        </div>
      </div>

      {seller.senderSignature?.confirmed === false && (
        <Callout variant="info">
          <Callout.Heading>Not yet verified</Callout.Heading>
          <Callout.Content>Please retry verifying your sender signature</Callout.Content>
        </Callout>
      )}

      {seller.senderSignature?.confirmed && (
        <Callout variant="success">
          <Callout.Heading>Verified</Callout.Heading>
          <Callout.Content>
            Your sender signature has been verified. Emails will be sent from{' '}
            <span className="font-medium">
              {seller.senderSignature?.name} {`<${seller.senderSignature?.emailAddress}>`}
            </span>
          </Callout.Content>
        </Callout>
      )}

      <div className="mt-4 space-x-2">
        {seller.senderSignature?.confirmed === false && (
          <Button appearance="primary" onClick={() => verify.mutate()}>
            Verify sender signature
            {verify.isLoading && <Spinner size="small" className="ml-2 text-white" />}
          </Button>
        )}

        <Button appearance="secondary" onClick={() => deleteSenderSignatureMutation.mutate()}>
          Delete sender signature
          {deleteSenderSignatureMutation.isLoading && (
            <Spinner size="small" className="ml-2 text-black" />
          )}
        </Button>

        {seller.senderSignature?.confirmed === true && (
          <Button appearance="secondary" onClick={() => sendTestEmailMutation.mutate()}>
            Send test email
            {sendTestEmailMutation.isLoading && (
              <Spinner size="small" className="ml-2 text-black" />
            )}
          </Button>
        )}
      </div>
    </div>
  );
}
